import { theme, ThemeConfig } from "antd";
import { DefaultTheme } from "styled-components";

// Base theme configuration
const baseThemeConfig: Partial<DefaultTheme> = {
  spacing: 4,
  cardRadius: "8px",
  cardRadius30: "30px",
  cardShadow: "0 0 2px 2px rgba(0,0,0,0.03)",
  direction: "ltr",
  status: {
    danger: "orange",
  },
  divider: "rgba(224, 224, 224, 1)",
  font: {
    family: ["Noto Sans", "Be Vietnam", "sans-serif"].join(","),
    weight: {
      light: 300,
      regular: 400,
      medium: 500,
      bold: 700,
    },
    size: {
      base: "14px",
      xs: "12px",
      sm: "12px",
      md: "14px",
      lg: "16px",
      xl: "18px",
      "2xl": "20px",
      "3xl": "24px",
      "4xl": "28px",
      "5xl": "32px",
      "6xl": "36px",
    },
  },
  breakpoints: {
    xs: "480",
    sm: "576",
    md: "768",
    lg: "992",
    xl: "1200",
    xxl: "1600",
  },
  sizes: {
    borderRadius: {
      base: "4px",
      circle: "50%",
    },
  },
};

// Light theme
export const lightTheme: Partial<DefaultTheme> = {
  ...baseThemeConfig,
  mode: "light",
  palette: {
    borderColor: "rgba(71, 71, 71, 0.14)",
    borderPrimaryColor: "#ffffff1f",
    dividerColor: "rgba(0, 0, 0, 0.06)",
    tooltipBg: "rgba(0, 0, 0, 0.75)",
    background: {
      paper: "rgb(250, 251, 253)",
      default: "rgb(244, 250, 255)",
      primary: "rgb(223, 227, 250)",
      secondary: "rgb(212, 238, 255)",
      sidebar:
        "linear-gradient(to right,rgb(243, 246, 255),rgb(255, 255, 255))",
      splitter: "rgb(222, 243, 255)",
    },
    text: {
      primary: "rgb(17, 24, 39)",
      secondary: "rgb(53, 57, 66)",
      disabled: "rgb(149, 156, 169)",
      hint: "rgb(174, 175, 184)",
    },
    switcher: {
      darkBg: "#1F2527",
      darkColor: "#cdf",
      lightBg: "#fff",
      lightColor: "#f6d447",
    },
    primary: {
      main: "#0A8FDC",
      contrastText: "#cdf",
    },
    secondary: {
      main: "#F04F47",
    },
    success: {
      main: "#52c41a",
      light: "#D9F5E5",
    },
    warning: {
      main: "#FF5252",
      light: "#FFECDC",
    },
    gray: {
      50: "#fafafa",
      100: "#F5F5F5",
      200: "#eeeeee",
      300: "#E0E0E0",
      400: "#bdbdbd",
      500: "#9e9e9e",
      600: "#757575",
      700: "#616161",
      800: "#201e21",
      900: "#212121",
      A100: "#d5d5d5",
      A200: "#aaaaaa",
      A400: "#303030",
      A700: "#616161",
    },
    black: "#000",
    white: "#FFFFFF",
    orange: {
      5: "#ffa940",
      6: "#fa8c16",
    },
    cyan: {
      7: "#08979c",
    },
    red: {
      6: "#f5222d",
    },
    green: {
      3: "#b7eb8f",
      5: "#73d13d",
      6: "#52c41a",
      7: "#389e0d",
    },
    blue: {
      5: "#40a9ff",
      7: "#096dd9",
    },
  },
};

// Dark theme
export const darkTheme: Partial<DefaultTheme> = {
  ...baseThemeConfig,
  mode: "dark",
  palette: {
    borderColor: "rgba(83, 83, 83, 0.5)",
    borderPrimaryColor: "#ffffff1f",
    dividerColor: "rgba(0, 0, 0, 0.06)",
    tooltipBg: "rgba(0, 0, 0, 0.75)",
    background: {
      paper: "rgba(43, 49, 55, 1)",
      default: "rgba(31, 37, 39, 1)",
      primary: "rgb(14, 17, 24)",
      secondary: "rgb(33, 41, 59)",
      sidebar: "linear-gradient(to right,rgb(37, 42, 44), #2B3137)",
      splitter: "rgb(21, 25, 44)",
    },
    text: {
      primary: "rgb(255,255,255)",
      secondary: "rgb(212, 210, 210)",
      disabled: "rgb(156, 163, 175)",
      hint: "rgb(174, 175, 184)",
    },
    switcher: {
      darkBg: "#1F2527",
      darkColor: "#cdf",
      lightBg: "#fff",
      lightColor: "#f6d447",
    },
    primary: {
      main: "#0A8FDC",
      contrastText: "#cdf",
    },
    secondary: {
      main: "#F04F47",
    },
    success: {
      main: "#52c41a",
      light: "#D9F5E5",
    },
    warning: {
      main: "#FF5252",
      light: "#FFECDC",
    },
    gray: {
      50: "#fafafa",
      100: "#F5F5F5",
      200: "#eeeeee",
      300: "#E0E0E0",
      400: "#bdbdbd",
      500: "#9e9e9e",
      600: "#757575",
      700: "#616161",
      800: "#201e21",
      900: "#212121",
      A100: "#d5d5d5",
      A200: "#aaaaaa",
      A400: "#303030",
      A700: "#616161",
    },
    black: "#000",
    white: "#FFFFFF",
    orange: {
      5: "#ffa940",
      6: "#fa8c16",
    },
    cyan: {
      7: "#08979c",
    },
    red: {
      6: "#f5222d",
    },
    green: {
      3: "#b7eb8f",
      5: "#73d13d",
      6: "#52c41a",
      7: "#389e0d",
    },
    blue: {
      5: "#40a9ff",
      7: "#096dd9",
    },
  },
};

// Get theme by mode
export const getTheme = (mode: "light" | "dark"): DefaultTheme => {
  return (mode === "light" ? lightTheme : darkTheme) as DefaultTheme;
};

// Map to Ant Design theme
export const mapToAntdTheme = (mode: "light" | "dark"): ThemeConfig => {
  const base = getTheme(mode);

  return {
    algorithm: mode === "dark" ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: base.palette.primary.main,
      colorSuccess: base.palette.success.main,
      colorWarning: base.palette.warning.main,
      colorError: base.palette.secondary.main,
      colorInfo: base.palette.primary.main,
      colorBgBase: base.palette.background.paper,
      colorBgContainer: base.palette.background.default,
      colorTextBase: base.palette.text.primary,
      colorText: base.palette.text.primary,
      colorTextSecondary: base.palette.text.secondary,
      colorTextTertiary: base.palette.text.disabled,
      colorTextQuaternary: base.palette.text.hint,
      colorBorder: base.palette.borderColor,
      colorBorderSecondary: base.palette.dividerColor,
      borderRadius: parseInt(base.cardRadius),
      fontFamily: base.font.family,
      fontSize: parseInt(base.font.size.base),
      boxShadow: base.cardShadow,
    },
    cssVar: {
      prefix: "paymenthub",
    },
  };
};
